import type { SignupQuestionConfig, SignupQuestionOption } from '@workspace/api-backend'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { FormField } from '@workspace/ui/components/form-field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { TextInput } from '@workspace/ui/components/text-input'
import { ToggleField } from '@workspace/ui/components/toggle-field'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

interface SignupQuestionFormProps {
  config: SignupQuestionConfig
  onSave: (config: SignupQuestionConfig) => void
  isSaving: boolean
}

const AUDIENCES: Record<SignupQuestionOption['audience'], string> = {
  both: 'Founders and investors',
  founder: 'Founders only',
  investor: 'Investors only',
}

const FOLLOW_UPS: Record<SignupQuestionOption['followUp']['type'], string> = {
  none: 'No follow-up',
  text: 'Short text box',
  choices: 'Row of choices',
}

/** A new option has no key until it is saved; the key is then made from its label and never changes. */
const NEW_OPTION: SignupQuestionOption = {
  key: '',
  label: '',
  investorLabel: '',
  audience: 'both',
  followUp: { type: 'none', prompt: '', choices: [] },
  aiIntroduced: false,
  pinLast: false,
}

/** "Choice A, Choice B" ⇄ ['Choice A', 'Choice B'] for the choices fields. */
const splitChoices = (text: string) =>
  text
    .split(',')
    .map((choice) => choice.trim())
    .filter(Boolean)

/** Lower-case letters, digits and underscores, made unique among the other keys. */
function keyFromLabel(label: string, taken: Set<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 50) || 'option'
  let key = base
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`
  return key
}

export function SignupQuestionForm({ config, onSave, isSaving }: SignupQuestionFormProps) {
  const [draft, setDraft] = useState(config)
  const [choiceText, setChoiceText] = useState(() =>
    draft.options.map((option) => option.followUp.choices.join(', '))
  )
  const [aiChoiceText, setAiChoiceText] = useState(draft.aiQuestion.choices.join(', '))

  const set = (patch: Partial<SignupQuestionConfig>) => setDraft((d) => ({ ...d, ...patch }))
  const setOption = (index: number, patch: Partial<SignupQuestionOption>) =>
    set({ options: draft.options.map((o, i) => (i === index ? { ...o, ...patch } : o)) })
  const move = (index: number, by: -1 | 1) => {
    const target = index + by
    if (target < 0 || target >= draft.options.length) return
    const swap = <T,>(list: T[]) => {
      const copy = [...list]
      ;[copy[index], copy[target]] = [copy[target] as T, copy[index] as T]
      return copy
    }
    set({ options: swap(draft.options) })
    setChoiceText(swap(choiceText))
  }
  const remove = (index: number) => {
    set({ options: draft.options.filter((_, i) => i !== index) })
    setChoiceText(choiceText.filter((_, i) => i !== index))
  }
  const add = () => {
    set({ options: [...draft.options, NEW_OPTION] })
    setChoiceText([...choiceText, ''])
  }

  const handleSave = () => {
    const taken = new Set(draft.options.map((o) => o.key).filter(Boolean))
    const options = draft.options.map((option, i) => {
      const key = option.key || keyFromLabel(option.label, taken)
      taken.add(key)
      const choices = option.followUp.type === 'choices' ? splitChoices(choiceText[i] ?? '') : []
      return { ...option, key, followUp: { ...option.followUp, choices } }
    })
    const saved = {
      ...draft,
      options,
      aiQuestion: { ...draft.aiQuestion, choices: splitChoices(aiChoiceText) },
    }
    setDraft(saved)
    onSave(saved)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-lg font-bold">Sign-up question</h1>
        <CtaButton type="button" onClick={handleSave} loading={isSaving}>
          Save
        </CtaButton>
      </div>

      <ToggleField
        label="Ask the question"
        description="Shown while a new founder's or investor's profile is analysed. Invited team members never see it."
        checked={draft.enabled}
        onChange={(enabled) => set({ enabled })}
      />
      <ToggleField
        label="Shuffle the options"
        description="Each person sees the options in a different order, so none wins by being first. Options marked 'Always last' stay at the end."
        checked={draft.shuffle}
        onChange={(shuffle) => set({ shuffle })}
      />

      <FormField label="Question for founders" required>
        <TextInput
          value={draft.founderTitle}
          onChange={(e) => set({ founderTitle: e.target.value })}
        />
      </FormField>
      <FormField label="Question for investors" required>
        <TextInput
          value={draft.investorTitle}
          onChange={(e) => set({ investorTitle: e.target.value })}
        />
      </FormField>

      <FormField
        label="Options"
        hint="History follows each option's key, which renaming keeps. For a different meaning, delete the option and add one with a different name."
      >
        <div className="space-y-3">
          {draft.options.map((option, i) => (
            <div
              key={option.key || `new-${i}`}
              className="space-y-3 rounded-lg border border-border bg-input/20 p-4"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput
                  value={option.label}
                  onChange={(e) => setOption(i, { label: e.target.value })}
                  placeholder="Label for founders"
                />
                <TextInput
                  value={option.investorLabel}
                  onChange={(e) => setOption(i, { investorLabel: e.target.value })}
                  placeholder="Label for investors (empty = same)"
                />
                <Select
                  value={option.audience}
                  onValueChange={(audience) =>
                    setOption(i, { audience: audience as SignupQuestionOption['audience'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(AUDIENCES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={option.followUp.type}
                  onValueChange={(type) =>
                    setOption(i, {
                      followUp: {
                        ...option.followUp,
                        type: type as SignupQuestionOption['followUp']['type'],
                      },
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FOLLOW_UPS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {option.followUp.type !== 'none' && (
                  <TextInput
                    value={option.followUp.prompt}
                    onChange={(e) =>
                      setOption(i, { followUp: { ...option.followUp, prompt: e.target.value } })
                    }
                    placeholder="Follow-up prompt, e.g. Which one? (optional)"
                  />
                )}
                {option.followUp.type === 'choices' && (
                  <TextInput
                    value={choiceText[i] ?? ''}
                    onChange={(e) =>
                      setChoiceText(choiceText.map((t, j) => (j === i ? e.target.value : t)))
                    }
                    placeholder="Choices, separated by commas"
                  />
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <span className="flex items-center gap-2">
                  <Checkbox
                    id={`option-${i}-ai`}
                    checked={option.aiIntroduced}
                    onCheckedChange={(checked) => setOption(i, { aiIntroduced: checked === true })}
                  />
                  <label htmlFor={`option-${i}-ai`}>
                    Means an AI introduced us (skips the AI question)
                  </label>
                </span>
                <span className="flex items-center gap-2">
                  <Checkbox
                    id={`option-${i}-last`}
                    checked={option.pinLast}
                    onCheckedChange={(checked) => setOption(i, { pinLast: checked === true })}
                  />
                  <label htmlFor={`option-${i}-last`}>Always last</label>
                </span>
              </div>

              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>Key: {option.key || 'set on save'}</span>
                <div className="flex gap-1">
                  <SecondaryButton
                    type="button"
                    size="sm"
                    icon={<ArrowUp />}
                    aria-label="Move up"
                    onClick={() => move(i, -1)}
                  />
                  <SecondaryButton
                    type="button"
                    size="sm"
                    icon={<ArrowDown />}
                    aria-label="Move down"
                    onClick={() => move(i, 1)}
                  />
                  <SecondaryButton
                    type="button"
                    size="sm"
                    icon={<Trash2 />}
                    aria-label="Delete option"
                    onClick={() => remove(i)}
                  />
                </div>
              </div>
            </div>
          ))}
          <SecondaryButton type="button" icon={<Plus />} onClick={add}>
            Add option
          </SecondaryButton>
        </div>
      </FormField>

      <ToggleField
        label="Ask about AI"
        description="A second question for people who did not pick an option marked as AI."
        checked={draft.aiQuestion.enabled}
        onChange={(enabled) => set({ aiQuestion: { ...draft.aiQuestion, enabled } })}
      />
      {draft.aiQuestion.enabled && (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="AI question" required className="sm:col-span-2">
            <TextInput
              value={draft.aiQuestion.text}
              onChange={(e) => set({ aiQuestion: { ...draft.aiQuestion, text: e.target.value } })}
            />
          </FormField>
          <FormField label="Yes button" required>
            <TextInput
              value={draft.aiQuestion.yesLabel}
              onChange={(e) =>
                set({ aiQuestion: { ...draft.aiQuestion, yesLabel: e.target.value } })
              }
            />
          </FormField>
          <FormField label="No button" required>
            <TextInput
              value={draft.aiQuestion.noLabel}
              onChange={(e) =>
                set({ aiQuestion: { ...draft.aiQuestion, noLabel: e.target.value } })
              }
            />
          </FormField>
          <FormField
            label="Assistants shown after Yes"
            hint="Separated by commas"
            className="sm:col-span-2"
          >
            <TextInput value={aiChoiceText} onChange={(e) => setAiChoiceText(e.target.value)} />
          </FormField>
        </div>
      )}
    </div>
  )
}
